import { useState, useEffect, useMemo } from "react";
import { Box, Alert, Typography, Chip, Stack, IconButton } from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import {
	MaterialReactTable,
	useMaterialReactTable,
	MRT_ExpandAllButton,
	MRT_SelectCheckbox,
	MRT_Row,
	MRT_TableInstance,
	MRT_RowSelectionState,
	MRT_VisibilityState,
	MRT_GroupingState,
	MRT_ColumnOrderState,
	MRT_ExpandedState,
	MRT_PaginationState,
	MRT_ColumnDef,
} from "material-react-table";
import { FileOptionTypes } from "../../../../../utilities/types";
import type { Job } from "../../../../../models";
import { ImportTableViewModel, JobsImportTableProps } from "../ImportTable.types";
import type { MergeConflict, MergeResolutions } from "../../../../../utilities/mergeJobs.types";
import { ImportTableToolbar } from "./ImportTable.desktop.toolbar";
import { getColumnVisibilityFromCompanySettings } from "../ImportTable.helpers";
import { MergeConflictModal } from "../../../shared/MergeConflictModal";
import { JobDetailModal } from "./JobDetailModal";
import { BulkEditModal } from "./BulkEditModal";
import { ValidationErrorsContext } from "../shared/hooks/ValidationErrorsContext";
import { useEditModeContext } from "../shared/hooks/EditModeContext";
import {
	column_Grower,
	column_Farm,
	column_Field,
	column_ReadyToSample,
	column_Deal,
	column_Density,
	column_Depth,
	column_TestPackage,
	column_FrequencyOfMicros,
	column_FieldPriority,
	column_Salesperson,
	column_SalespersonPhone,
	column_Branch,
	column_LastCrop,
	column_NextCrop,
	column_YieldGoal,
	column_EstimatedFieldReadyDate,
	column_BoundaryGeoJSON,
	column_PointsGeoJSON,
	column_ZonesGeoJSON,
	column_LabSubmissionCode,
	column_LabInstructions,
	column_GrowerPhone,
	column_SubmissionNotes,
	column_SubmitterName,
	column_SubmitterEmail,
	column_FieldId,
	column_EventId,
	column_CollectBoundary,
	column_IsTestJob,
	column_MicrosList,
	column_FieldNotes,
	column_LabName,
	column_ExternalLabId,
	column_ExternalId,
	column_ExternalFarmId,
} from "../columns";
import "../ImportTable.css";
import "./ImportTable.desktop.css";

export interface ImportTableDesktopProps {
	vm: ImportTableViewModel;
	props: JobsImportTableProps;
}

export function ImportTableDesktop({ vm, props }: ImportTableDesktopProps) {
	// ============= MRT STATE =============
	const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
	const [expanded, setExpanded] = useState<MRT_ExpandedState>(true);
	const [pagination, setPagination] = useState<MRT_PaginationState>({
		pageIndex: 0,
		pageSize: 20,
	});
	const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
		getColumnVisibilityFromCompanySettings(props.selectedCompany, props.user, props.deals?.[0]),
	);
	const [grouping, setGrouping] = useState<MRT_GroupingState>(["grower"]);
	const [, setColumnOrder] = useState<MRT_ColumnOrderState>(["field", "grower", "farm"]);

	// ============= MERGE CONFLICT MODAL STATE =============
	const [mergeConflictModalOpen, setMergeConflictModalOpen] = useState(false);
	const [pendingMergeIndices, setPendingMergeIndices] = useState<number[]>([]);
	const [mergeConflicts, setMergeConflicts] = useState<MergeConflict[]>([]);

	// ============= JOB DETAIL MODAL STATE =============
	const [jobDetailModalOpen, setJobDetailModalOpen] = useState(false);
	const [selectedJobForEdit, setSelectedJobForEdit] = useState<Job | null>(null);

	// ============= BULK EDIT MODAL STATE =============
	const [bulkEditModalOpen, setBulkEditModalOpen] = useState(false);

	// ============= EDIT MODE CONTEXT =============
	const { toggleRowEdit, editingRowIds, exitAllEditMode } = useEditModeContext();

	// Update column visibility when company changes
	useEffect(() => {
		setColumnVisibility(getColumnVisibilityFromCompanySettings(props.selectedCompany, props.user, props.deals?.[0]));
	}, [props.selectedCompany, props.user, props.deals]);

	// Legacy: setDrawerState is now a no-op with modal implementation
	useEffect(() => {
		if (props.isMapVisible && Object.keys(rowSelection).length > 0) {
			props.setDrawerState?.(true);
		}
	}, [props.isMapVisible]);

	// Re-send displayed jobs when jobs data changes (e.g., after merge/edit)
	useEffect(() => {
		if (Object.keys(rowSelection).length > 0 && props.setDisplayedJobs) {
			vm.updateDisplayedJobs(rowSelection);
		}
	}, [vm.jobs]);

	// ============= CALLBACKS =============
	const getSelectedIndices = () => {
		const selectedJobIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
		return selectedJobIds.map((jobId) => vm.jobs.findIndex((job) => job.id === jobId)).filter((index) => index !== -1);
	};

	const deleteSelectedJobs = () => {
		const indices = getSelectedIndices();
		const count = indices.length;
		if (!window.confirm(`Are you sure you want to delete ${count} job${count === 1 ? "" : "s"}?`)) {
			return;
		}

		vm.deleteJobs(indices);
		setRowSelection({});
	};

	const duplicateSelectedJobs = () => {
		const indices = getSelectedIndices();
		vm.duplicateJobs(indices);
		setRowSelection({});
	};

	const mergeSelectedJobs = () => {
		const indices = getSelectedIndices();
		if (indices.length < 2) return;

		const count = indices.length;
		if (!window.confirm(`Are you sure you want to merge ${count} jobs?`)) {
			return;
		}

		const conflicts = vm.detectMergeConflicts(indices);

		if (conflicts.length > 0) {
			setPendingMergeIndices(indices);
			setMergeConflicts(conflicts);
			setMergeConflictModalOpen(true);
		} else {
			vm.mergeJobs(indices);
			selectMergedJob(indices);
		}
	};

	const selectMergedJob = (indices: number[]) => {
		const sortedIndices = [...indices].sort((a, b) => a - b);
		const mergedJobIndex = sortedIndices[0];
		const mergedJob = vm.jobs[mergedJobIndex];
		if (!mergedJob) return;

		const newSelection: MRT_RowSelectionState = { [mergedJob.id]: true };
		setRowSelection(newSelection);
	};

	const handleMergeConflictConfirm = (resolutions: MergeResolutions) => {
		vm.mergeJobs(pendingMergeIndices, resolutions);
		selectMergedJob(pendingMergeIndices);
		setMergeConflictModalOpen(false);
		setPendingMergeIndices([]);
		setMergeConflicts([]);
	};

	const handleMergeConflictCancel = () => {
		setMergeConflictModalOpen(false);
		setPendingMergeIndices([]);
		setMergeConflicts([]);
	};

	// ============= JOB DETAIL MODAL HANDLERS =============
	const handleOpenJobDetailModal = (job: Job) => {
		setSelectedJobForEdit(job);
		setJobDetailModalOpen(true);
	};

	const handleCloseJobDetailModal = () => {
		setJobDetailModalOpen(false);
		setSelectedJobForEdit(null);
	};

	const handleSaveJobDetailModal = (updatedJob: Job) => {
		const updatedJobs = vm.jobs.map((j) => (j.id === updatedJob.id ? updatedJob : j));
		vm.columnContext.setJobs(updatedJobs);
		vm.columnContext.validateJobs([updatedJob]);
		handleCloseJobDetailModal();
	};

	const handleDeleteJobFromModal = (jobId: string): boolean => {
		const index = vm.jobs.findIndex((j) => j.id === jobId);
		if (index === -1) return false;

		if (!window.confirm("Are you sure you want to delete this job?")) {
			return false;
		}

		vm.deleteJobs([index]);
		return true;
	};

	const handleDuplicateJobFromModal = (jobId: string) => {
		const index = vm.jobs.findIndex((j) => j.id === jobId);
		if (index === -1) return;

		vm.duplicateJobs([index]);
	};

	// ============= BULK EDIT MODAL HANDLERS =============
	const getSelectedJobs = (): Job[] => {
		const selectedJobIds = Object.keys(rowSelection).filter((key) => rowSelection[key]);
		return selectedJobIds
			.map((jobId) => vm.jobs.find((job) => job.id === jobId))
			.filter((job): job is Job => job !== undefined);
	};

	const handleOpenBulkEditModal = () => {
		const selectedJobs = getSelectedJobs();
		if (selectedJobs.length < 2) return;
		setBulkEditModalOpen(true);
	};

	const handleCloseBulkEditModal = () => {
		setBulkEditModalOpen(false);
	};

	const handleApplyBulkEdit = (updatedJobs: Job[]) => {
		const updatedJobMap = new Map(updatedJobs.map((j) => [j.id, j]));
		const newJobs = vm.jobs.map((j) => updatedJobMap.get(j.id) ?? j);
		vm.columnContext.setJobs(newJobs);
		vm.columnContext.validateJobs(updatedJobs);
		handleCloseBulkEditModal();
	};

	const getSelectedJobId = (): string | undefined => {
		const selectedJobIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
		if (selectedJobIds.length !== 1) return undefined;
		return selectedJobIds[0];
	};

	const splitSelectedJob = () => {
		const selectedJobIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
		if (selectedJobIds.length !== 1) return;
		const jobIndex = vm.jobs.findIndex((job) => job.id === selectedJobIds[0]);
		if (jobIndex === -1) return;
		if (!props.selectedFeatureIds?.size) return;

		vm.splitJob(jobIndex, props.selectedFeatureIds, props.onClearFeatureSelection);
		setRowSelection({});
	};

	// ============= COLUMNS =============
	const columns = useMemo<MRT_ColumnDef<Job>[]>(
		() => [
			// ==========================================================================================
			// IMPORT_FIELDS: If you are adding a new column, please also add it to all other places with this comment.
			// Maintain the same order in all places for consistency.
			// ==========================================================================================
			// Basic Information
			column_Grower(vm.columnContext),
			column_Farm(vm.columnContext),
			column_Field(vm.columnContext),

			// Attachments
			column_BoundaryGeoJSON(vm.columnContext),
			column_PointsGeoJSON(vm.columnContext),
			column_ZonesGeoJSON(vm.columnContext),
			column_FieldNotes(vm.columnContext),

			// Order
			column_ReadyToSample(vm.columnContext),
			column_EstimatedFieldReadyDate(vm.columnContext),
			column_FieldPriority(vm.columnContext),
			column_CollectBoundary(vm.columnContext),
			column_SubmissionNotes(vm.columnContext),

			// Deal
			column_Density(vm.columnContext),
			column_Depth(vm.columnContext),
			column_LabName(vm.columnContext),
			column_TestPackage(vm.columnContext),
			column_FrequencyOfMicros(vm.columnContext),
			column_Deal(vm.columnContext),

			// Lab Info
			column_LabInstructions(vm.columnContext),
			column_MicrosList(vm.columnContext),
			column_LastCrop(vm.columnContext),
			column_NextCrop(vm.columnContext),
			column_YieldGoal(vm.columnContext),

			// Contacts
			column_Branch(vm.columnContext),
			column_Salesperson(vm.columnContext),
			column_SalespersonPhone(vm.columnContext),
			column_GrowerPhone(vm.columnContext),
			column_SubmitterName(vm.columnContext),
			column_SubmitterEmail(vm.columnContext),

			// Sync Codes
			column_EventId(vm.columnContext),
			column_LabSubmissionCode(vm.columnContext),
			column_FieldId(vm.columnContext),
			column_ExternalId(vm.columnContext),
			column_ExternalFarmId(vm.columnContext),
			column_ExternalLabId(vm.columnContext),

			// Misc
			column_IsTestJob(vm.columnContext),
		],
		[vm.columnContext],
	);

	// ============= TABLE CONFIGURATION =============
	const table = useMaterialReactTable({
		columns,
		data: vm.jobs,
		getRowId: (row) => row.id,
		enableColumnFilterModes: true,
		enableColumnOrdering: true,
		enableGrouping: true,
		enableColumnPinning: true,
		enableColumnResizing: true,
		enableColumnActions: true,
		enableColumnDragging: false,
		enableCellActions: true,
		enableRowSelection: !!props.allowMap,
		enableEditing: true,
		editDisplayMode: "table",
		onCreatingRowCancel: () => vm.setValidationErrors({}),
		onEditingRowCancel: () => vm.setValidationErrors({}),
		enableRowActions: true,
		positionActionsColumn: "last",
		renderRowActions: ({ row }) => {
			if (row.subRows && row.subRows.length > 0) {
				return null;
			}
			return (
				<IconButton size="small" onClick={() => handleOpenJobDetailModal(row.original)} aria-label="edit job">
					<EditIcon fontSize="small" />
				</IconButton>
			);
		},
		groupedColumnMode: false,
		autoResetPageIndex: false,
		onExpandedChange: (isExpanded) => {
			setExpanded(isExpanded);
		},
		enableStickyFooter: true,
		enableRowVirtualization: false,
		enableColumnVirtualization: false,
		enablePagination: true,
		state: {
			rowSelection,
			columnVisibility,
			grouping,
			expanded,
			density: "compact",
			pagination,
		},
		onPaginationChange: (pagination) => {
			setPagination(pagination);
		},
		onRowSelectionChange: (rows) => {
			const newRowSelection = typeof rows === "function" ? rows(rowSelection) : rows;
			const hasSelection = Object.keys(newRowSelection).length > 0;

			props.setDrawerState(hasSelection);
			setRowSelection(newRowSelection);

			if (props.setDisplayedJobs) {
				if (hasSelection) {
					vm.updateDisplayedJobs(newRowSelection);
				} else {
					props.setDisplayedJobs([]);
				}
			}
		},
		displayColumnDefOptions: {
			"mrt-row-select": {
				Header: ({ table }: { table: MRT_TableInstance<Job> }) => <MRT_SelectCheckbox table={table} />,
				Cell: ({ row, table }: { row: MRT_Row<Job>; table: MRT_TableInstance<Job> }) => (
					<MRT_SelectCheckbox row={row} table={table} />
				),
				size: 60,
			},
			"mrt-row-expand": {
				Header: () => (
					<Stack direction="row" alignItems="center" color={"secondary"} className="row-expand-header">
						<MRT_ExpandAllButton table={table} />
						<Box>Groups</Box>
					</Stack>
				),
				Cell: ({ row }: { row: MRT_Row<Job> }) => {
					if (row.subRows && row.subRows.length > 0) {
						return <span style={{ fontSize: "0.85rem" }}>{row.original.importGrowerName}</span>;
					}
					const job = row.original;
					return (
						<Box sx={{ display: "flex", flexDirection: "column", fontSize: "0.85rem" }}>
							<span>{job.importGrowerName}</span>
							<span>{job.importFieldName}</span>
							<span style={{ color: "#666" }}>{job.importFarmName}</span>
						</Box>
					);
				},
				GroupedCell: ({ row, table }) => {
					const { grouping } = table.getState();
					return row.getValue(grouping[grouping.length - 1]);
				},
				enableResizing: true,
				size: 200,
			},
			"mrt-row-actions": {
				header: "Edit",
				size: 60,
			},
		},
		onColumnVisibilityChange: (column) => {
			setColumnVisibility(column);
		},
		onColumnOrderChange: (columns) => {
			setColumnOrder(columns);
		},
		onGroupingChange: (grouping) => {
			setGrouping(grouping);
		},
		muiTableBodyRowProps: ({ row }) => ({
			title: !editingRowIds.has(row.id) ? "Double-Click to Edit" : undefined,
			onClick: (e: React.MouseEvent) => {
				if (editingRowIds.has(row.id) || row.subRows?.length) {
					e.stopPropagation();
				}
			},
			onDoubleClick: () => {
				toggleRowEdit(row.id);
			},
			sx: {
				cursor: "pointer",
				...(editingRowIds.has(row.id) && {
					backgroundColor: "action.selected",
					"& td": { backgroundColor: "inherit" },
				}),
			},
		}),
		muiTableBodyCellProps: ({ row }) => ({
			sx: {
				backgroundColor: row.depth === 0 ? "#d4d5d6" : editingRowIds.has(row.id) ? "inherit" : "inherit",
				padding: "2px 4px",
			},
		}),
		muiTableHeadCellProps: {
			sx: {
				padding: "2px 4px",
			},
		},
		muiTableContainerProps: {
			sx: {
				maxHeight: "calc(100vh - 300px)",
				flex: 1,
				minHeight: 0,
			},
		},
		layoutMode: "grid",
		initialState: {
			showGlobalFilter: true,
			grouping,
			pagination: {
				pageIndex: 0,
				pageSize: 20,
			},
			columnPinning: {
				left: ["mrt-row-select", "mrt-row-expand"],
				right: ["mrt-row-actions"],
			},
		},
		paginationDisplayMode: "pages",
		positionToolbarAlertBanner: "bottom",
		muiSearchTextFieldProps: {
			size: "small",
			variant: "outlined",
		},
		muiPaginationProps: {
			color: "secondary",
			rowsPerPageOptions: [20, 50, 100, 200, 500],
			shape: "rounded",
			variant: "outlined",
		},
		renderTopToolbar: ({ table }) => (
			<ImportTableToolbar
				table={table}
				jobs={vm.jobs}
				mapDisplayMode={props.mapDisplayMode}
				setMapDisplayMode={props.setMapDisplayMode}
				isMapVisible={props.isMapVisible}
				setIsMapVisible={props.setIsMapVisible}
				hasSelectedRows={Object.keys(rowSelection).length > 0}
				selectedRowCount={Object.keys(rowSelection).length}
				selectedFeatureIds={props.selectedFeatureIds}
				selectedJobId={getSelectedJobId()}
				importCSV={vm.importCSV}
				csvImportPending={vm.csvImportPending}
				confirmCSVImport={vm.confirmCSVImport}
				cancelCSVImport={vm.cancelCSVImport}
				csvImportErrors={vm.csvImportErrors}
				dismissCsvImportErrors={vm.dismissCsvImportErrors}
				hasValidationErrors={vm.hasValidationErrors}
				exportCSV={vm.exportCSV}
				createJobs={vm.createJobs}
				addBlankJob={vm.addBlankJob}
				deleteSelectedJobs={deleteSelectedJobs}
				duplicateSelectedJobs={duplicateSelectedJobs}
				mergeSelectedJobs={mergeSelectedJobs}
				splitSelectedJob={splitSelectedJob}
				bulkEditSelectedJobs={handleOpenBulkEditModal}
			/>
		),
	});

	// ============= DESKTOP VIEW =============
	return (
		<ValidationErrorsContext.Provider value={vm.validationErrors}>
			<div id="bulk-import-table" className="import-table" data-testid="import-table-desktop" onClick={exitAllEditMode}>
				{/* Stats Notice */}
				{(vm.jobs.length > 0 || props.featureStats) && (
					<Alert severity="info" className="stats-alert" icon={false}>
						<Box className="stats-container">
							{/* Jobs count */}
							<Box>
								<Typography variant="body2" component="span" className="label">
									Jobs:
								</Typography>{" "}
								<Typography variant="body2" component="span">
									{vm.jobs.length}
								</Typography>{" "}
							</Box>

							{/* Feature stats */}
							{props.featureStats && (
								<Box className="feature-stats">
									<Box className="chips-container">
										{props.featureStats.zones > 0 && (
											<Chip
												label={`${FileOptionTypes.Zones.label}: ${props.featureStats.zones}`}
												size="small"
												variant="outlined"
											/>
										)}
										{props.featureStats.centroids > 0 && (
											<Chip
												label={`${FileOptionTypes.Centroids.label}: ${props.featureStats.centroids}`}
												size="small"
												variant="outlined"
											/>
										)}
										{props.featureStats.cores > 0 && (
											<Chip
												label={`${FileOptionTypes.Cores.label}: ${props.featureStats.cores}`}
												size="small"
												variant="outlined"
											/>
										)}
									</Box>
								</Box>
							)}

							{/* Validation errors */}
							{Object.keys(vm.validationErrors).length > 0 &&
								(() => {
									const summary = vm.getValidationErrorSummary();
									return (
										<>
											{summary.missing.length > 0 && (
												<Box className="validation-errors">
													<Typography variant="body2" component="span" className="error-label">
														Missing:
													</Typography>
													{summary.missing.map((error, index) => (
														<Chip
															key={index}
															label={`${error.field}: ${error.count}`}
															size="small"
															color="error"
															variant="outlined"
														/>
													))}
												</Box>
											)}
											{summary.invalid.length > 0 && (
												<Box className="validation-errors">
													<Typography variant="body2" component="span" className="error-label">
														Invalid:
													</Typography>
													{summary.invalid.map((error, index) => (
														<Chip
															key={index}
															label={`${error.field}: ${error.count}`}
															size="small"
															color="error"
															variant="outlined"
														/>
													))}
												</Box>
											)}
										</>
									);
								})()}
						</Box>
					</Alert>
				)}
				<MaterialReactTable table={table} />
				<MergeConflictModal
					open={mergeConflictModalOpen}
					conflicts={mergeConflicts}
					onConfirm={handleMergeConflictConfirm}
					onCancel={handleMergeConflictCancel}
					salespersons={props.salespersons}
					branches={props.branches}
					deals={props.allDeals}
				/>
				<JobDetailModal
					open={jobDetailModalOpen}
					onClose={handleCloseJobDetailModal}
					onSave={handleSaveJobDetailModal}
					onDelete={handleDeleteJobFromModal}
					onDuplicate={handleDuplicateJobFromModal}
					job={selectedJobForEdit}
					company={props.selectedCompany}
					allDeals={props.allDeals}
					salespersons={props.salespersons}
					branches={props.branches}
					user={props.user}
					lastCropOptions={props.lastCropOptions}
					nextCropOptions={props.nextCropOptions}
					integration={props.integration}
				/>
				<BulkEditModal
					open={bulkEditModalOpen}
					onClose={handleCloseBulkEditModal}
					onApply={handleApplyBulkEdit}
					jobs={getSelectedJobs()}
					company={props.selectedCompany}
					allDeals={props.allDeals}
					salespersons={props.salespersons}
					branches={props.branches}
					user={props.user}
					lastCropOptions={props.lastCropOptions}
					nextCropOptions={props.nextCropOptions}
					integration={props.integration}
				/>
			</div>
		</ValidationErrorsContext.Provider>
	);
}
